import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/shared/constants/routes';
import { Icons } from '@/shared/constants/icons';
import { formatDateTime } from '@/shared/lib/intl';
import { usePostConversations, usePostCustomerCounts } from '@/shared/hooks/useCustomersReport';
import type { ReportCounts } from '@/shared/services/reports.service';
import { ConfidenceDot } from './ConfidenceDot';
import { PlatformMark } from './PlatformMark';
import type { PlatformId } from './PlatformIcon';

/** "14 enquiries · 3 bookings": a post's confirmed customers, in words. */
function broughtLine(c: ReportCounts, t: (k: string, o?: Record<string, unknown>) => string): string {
  return [
    t('customers.posts.enquiries', { count: c.enquiries }),
    t('customers.posts.bookings', { count: c.bookings + c.orders }),
    ...(c.sales > 0 ? [t('customers.posts.sales', { count: c.sales })] : []),
  ].join(' · ');
}

/**
 * In a post's receipt (Task 3.3): "Brought 14 enquiries · 3 bookings", likely
 * ones apart, a link to the report, and the conversations the post started,
 * each opening in the inbox. Nothing while insights.customers is off or the
 * post brought no one.
 */
export function PostCustomers({ postId }: { postId: string }): ReactElement | null {
  const { t } = useTranslation();
  const counts = usePostCustomerCounts([postId]).data?.[postId];
  const conversations = usePostConversations(postId).data ?? [];
  if (!counts || counts.confirmed.conversations + counts.likely.conversations === 0) return null;
  const likely = counts.likely.conversations;
  return (
    <section className="flex flex-col gap-2 rounded-xl p-3 bg-[var(--human-soft)]" aria-labelledby="post-customers-title" data-testid="post-customers">
      <h3 id="post-customers-title" className="m-0 text-body font-semibold text-[var(--human-text)]">
        {t('customers.receipt.brought', { what: broughtLine(counts.confirmed, t) })}
      </h3>
      {likely > 0 && (
        <span className="inline-flex items-center gap-1 text-meta text-[var(--ink-2)]">
          <ConfidenceDot confidence="likely" compact />
          {t('customers.posts.likely', { count: likely })}
        </span>
      )}
      {conversations.length > 0 && (
        <ul className="m-0 p-0 list-none flex flex-col gap-1">
          {conversations.slice(0, 10).map((c) => (
            <li key={c.key}>
              <Link to={`${ROUTES.INBOX}?conversation=${encodeURIComponent(c.key)}`} className="flex items-center gap-2 min-h-[40px] text-ui text-[var(--ink)] no-underline hover:underline">
                <PlatformMark platform={c.platform.toLowerCase() as PlatformId} size={16} />
                <span className="flex-1 min-w-0 truncate" dir="auto">{c.peer || t('customers.receipt.someone')}</span>
                <span className="text-meta text-[var(--ink-3)]">{formatDateTime(c.startedAt)}</span>
                <ConfidenceDot confidence={c.confidence} compact />
              </Link>
            </li>
          ))}
        </ul>
      )}
      <Link to={ROUTES.ANALYTICS} className="text-ui font-semibold text-[var(--color-accent-text)] w-fit">{t('customers.receipt.report')}</Link>
    </section>
  );
}

/** The conversations pill on a published post that started conversations: confirmed, then "+N" likely apart. */
export function ConversationsBadge({ counts }: { counts: { confirmed: ReportCounts; likely: ReportCounts } | undefined }): ReactElement | null {
  const { t } = useTranslation();
  if (!counts) return null;
  const confirmed = counts.confirmed.conversations;
  const likely = counts.likely.conversations;
  if (confirmed + likely === 0) return null;
  return (
    <span
      className="inline-flex items-center gap-0.5 rounded-full px-1.5 text-caption font-semibold shrink-0 bg-[var(--human-soft)] text-[var(--human-text)]"
      title={t('customers.badge.title', { confirmed, likely })}
      aria-label={t('customers.badge.title', { confirmed, likely })}
      data-testid="conversations-badge"
    >
      <Icons.comment size={12} strokeWidth={1.5} aria-hidden="true" />
      <span aria-hidden="true">{confirmed}</span>
      {likely > 0 && <span aria-hidden="true" className="opacity-75">+{likely}</span>}
    </span>
  );
}
